import React from "react";
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import {
  Card,
  CardBody,
  Button,
  InputGroup,
  FormInput
} from "shards-react";
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';

import PageTitle from "../../../components/common/PageTitle";
import { QNA_CATEGORIES, getQnACategory, QNA_CATEGORY_FALLBACK_LABEL } from "./qnaCategories";

/**
 * QnA (고객용)
 * - 관리자(/qnaList) 가 미리 등록해 둔 자주 묻는 질문/답변을 read-only 로 조회/검색.
 * - boardName: 'qna' 로 구분, status: 'published' 인 항목만 노출.
 * - 검색은 title + content 키워드 OR 매칭 (백엔드 getBoardList 리졸버에서 처리).
 * - 아코디언 단일 펼침 — 다른 항목 펼치면 이전 항목 자동 닫힘.
 */
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ board }) => ({ getBoardList: board.getBoardList }))
@observer
class QnA extends React.Component {

  @observable isLoading = false;
  @observable list = { docs: [], totalDocs: 0 };
  @observable keyword = '';
  @observable expandedId = null;
  @observable categoryFilter = ''; // '' = 전체
  @observable listParams = {
    page: 1,
    limit: 100,
    sort: '-isPinned -createdAt',
    searchParams: {
      boardName: 'qna',
      status: 'published'
    }
  };

  componentDidMount() {
    this.getList();
  }

  @action
  getList = async () => {
    this.isLoading = true;
    try {
      const res = await this.props.getBoardList(this.listParams);
      this.list = res || { docs: [], totalDocs: 0 };
    } catch (e) {
      console.error('[QnA] getList error:', e);
      this.list = { docs: [], totalDocs: 0 };
    }
    this.isLoading = false;
  };

  @action
  onChangeKeyword = (event) => {
    this.keyword = event.target.value;
  };

  @action
  onSubmitSearch = () => {
    this.listParams.searchParams.keyword = (this.keyword || '').trim();
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  @action
  onClearKeyword = () => {
    this.keyword = '';
    this.listParams.searchParams.keyword = '';
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  @action
  onChangeCategoryFilter = (value) => {
    this.categoryFilter = value || '';
    if (this.categoryFilter) {
      this.listParams.searchParams.inquiryType = this.categoryFilter;
    } else {
      delete this.listParams.searchParams.inquiryType;
    }
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  @action
  toggleExpand = (id) => {
    this.expandedId = this.expandedId === id ? null : id;
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.onSubmitSearch();
    }
  };

  render() {
    const docs = (this.list && this.list.docs) || [];
    const hasKeyword = !!(this.listParams.searchParams.keyword || '').trim();

    return (
      <div className="main-content">
        <PageTitle />

        {/* ===== 검색 + 분류 칩 ===== */}
        <Card small style={{ padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InputGroup style={{ flex: 1 }}>
              <FormInput
                placeholder="궁금한 키워드로 검색하세요 (제목·내용)"
                value={this.keyword}
                onChange={this.onChangeKeyword}
                onKeyDown={this.handleKeyDown}
                style={{ fontSize: 14 }}
              />
            </InputGroup>
            <Button
              theme="primary"
              onClick={this.onSubmitSearch}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FeatherIcon icon="search" size="14" /> 검색
            </Button>
            {hasKeyword && (
              <Button
                theme="light"
                onClick={this.onClearKeyword}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title="검색 초기화"
              >
                <FeatherIcon icon="x" size="14" /> 초기화
              </Button>
            )}
          </div>
          {/* 분류 칩 — 전체 + 카테고리들 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            {[{ value: '', label: '전체' }, ...QNA_CATEGORIES].map((c) => {
              const active = this.categoryFilter === c.value;
              return (
                <button
                  key={c.value || 'all'}
                  type="button"
                  onClick={() => this.onChangeCategoryFilter(c.value)}
                  style={{
                    padding: '5px 14px', fontSize: 12.5, fontWeight: active ? 700 : 500,
                    border: '1px solid ' + (active ? '#1F2937' : '#E5E7EB'),
                    background: active ? '#1F2937' : '#FFFFFF',
                    color: active ? '#FFFFFF' : '#374151',
                    borderRadius: 999, cursor: 'pointer'
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Card>

        {/* ===== 결과 카운트 ===== */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 4px' }}>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            총 <b style={{ color: '#1F2937' }}>{this.list.totalDocs || 0}</b>건
            {hasKeyword && (
              <span style={{ marginLeft: 8, color: '#9CA3AF' }}>— "{this.listParams.searchParams.keyword}" 검색</span>
            )}
          </div>
          {this.isLoading && <span style={{ fontSize: 12, color: '#9CA3AF' }}>불러오는 중…</span>}
        </div>

        {/* ===== 리스트 (아코디언) ===== */}
        <Card small>
          <CardBody style={{ padding: 0 }}>
            {docs.length === 0 ? (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                {hasKeyword ? '검색 결과가 없습니다.' : '등록된 QnA가 없습니다.'}
              </div>
            ) : (
              <div>
                {docs.map((item, i) => {
                  const id = item._id;
                  const isOpen = this.expandedId === id;
                  return (
                    <div
                      key={id || i}
                      style={{
                        borderBottom: i === docs.length - 1 ? 'none' : '1px solid #F3F4F6',
                        background: isOpen ? '#FAFBFC' : '#FFFFFF'
                      }}
                    >
                      {/* 질문(Q) — 클릭하면 답변 펼침 */}
                      <button
                        type="button"
                        onClick={() => this.toggleExpand(id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          width: '100%', padding: '16px 20px',
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', textAlign: 'left'
                        }}
                      >
                        {item.isPinned && (
                          <FeatherIcon icon="bookmark" size="14" style={{ color: '#F59E0B', flexShrink: 0 }} />
                        )}
                        <span
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#EFF6FF', color: '#2563EB',
                            fontSize: 12, fontWeight: 700, flexShrink: 0
                          }}
                        >
                          Q
                        </span>
                        {/* 분류 배지 */}
                        {(() => {
                          const meta = getQnACategory(item.inquiryType);
                          if (!meta && !item.inquiryType) return null;
                          const bg = meta ? meta.bg : '#F3F4F6';
                          const color = meta ? meta.color : '#6B7280';
                          const label = meta ? meta.label : QNA_CATEGORY_FALLBACK_LABEL;
                          return (
                            <span style={{
                              padding: '2px 8px', fontSize: 11, fontWeight: 600,
                              background: bg, color, borderRadius: 4, flexShrink: 0
                            }}>{label}</span>
                          );
                        })()}
                        <span style={{ flex: 1, fontSize: 14, color: '#1F2937', fontWeight: isOpen ? 700 : 500 }}>
                          {item.title || '(제목 없음)'}
                        </span>
                        <FeatherIcon
                          icon={isOpen ? 'chevron-up' : 'chevron-down'}
                          size="16"
                          style={{ color: '#9CA3AF', flexShrink: 0 }}
                        />
                      </button>

                      {/* 답변(A) — 펼침 영역 */}
                      {isOpen && (
                        <div style={{ padding: '0 20px 18px 54px' }}>
                          <div style={{
                            display: 'flex', alignItems: 'flex-start', gap: 12,
                            background: '#F9FAFB', borderLeft: '3px solid #10B981',
                            borderRadius: 6, padding: '14px 16px'
                          }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 22, height: 22, borderRadius: '50%',
                              background: '#D1FAE5', color: '#047857',
                              fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1
                            }}>A</span>
                            <div style={{
                              flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.7,
                              color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                            }}>
                              {item.content || '(답변이 등록되지 않았습니다.)'}
                            </div>
                          </div>
                          {item.updatedAt && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                              마지막 수정: {moment(item.updatedAt).format('YYYY.MM.DD HH:mm')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    );
  }
}

export default QnA;
